import { useEffect } from 'react';
import { Group, Typography, Wrapper } from '@screentone/core';

import { WidePageWrapper } from 'components';
import EmbargoWarning from 'components/embargo-warning/EmbargoWarning';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { OffPlatformListContainer } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import MultiTabSection from './components/off-platform-collection-multitab-section/OffPlatformCollectionMultitabSection';
import PublishingButtons from './components/off-platform-collection-publishing-buttons/OffPlatformCollectionPublishingButtons';
import { useOffPlatformPublish } from './hooks/useOffPlatformPublish';
import styles from './OffPlatformEdit.module.scss';

export const OffPlatformEdit = ({ onUpdate }: { onUpdate: () => void }) => {
  const {
    root: data,
    metadata,
    renderEntity,
    hasModelChangedOnce,
    resetModelChanged,
    setNewMetadata,
    getSortedEmbargoContent
  } = useDataModelContext<OffPlatformListContainer>();

  const latestEmbargoContent = getSortedEmbargoContent('desc').at(0);

  const { data: publicationSettingsResp } = usePublicationSettings();

  const {
    isMutationInProgress,
    isInEditMode,
    notificationText,
    handlePublish,
    handleSchedule,
    hideEditSchedule,
    showEditSchedule,
    handleUpdateSchedule,
    handleReschedule,
    savedForScheduleEdit,
    handleDeleteSchedule,
    isRecentlyUpdated,
    isRecentlyPublish,
    isScheduling,
    isPublishing,
    isDeleting
  } = useOffPlatformPublish();

  useEffect(() => {
    if (isRecentlyUpdated) {
      onUpdate();
    }
  }, [isRecentlyUpdated, onUpdate]);

  const handleScheduleClick = async (publishUtc: number) => {
    if (isInEditMode) {
      await handleReschedule(publishUtc);
    } else {
      await handleSchedule(publishUtc);
    }
  };

  const handleClickPublish = async () => {
    if (isInEditMode) {
      await handleUpdateSchedule();
    } else {
      await handlePublish();
    }

    resetModelChanged();
  };

  const handleUpdateName = (name: string) => {
    setNewMetadata({ name });
  };

  return (
    <WidePageWrapper padding={{ all: 'md' }}>
      <WidePageWrapper.Header>
        <Wrapper padding={{ top: 'md', bottom: 'md' }} className={styles.secondHeader}>
          <Group gap="sm">
            <Typography variant="header2" margin={{ bottom: 'none' }} data-testid="off-platform-header-title">
              {metadata.name}
            </Typography>
            {notificationText && (
              <Typography color="asphalt" margin={{ left: 'md' }}>
                {notificationText}
              </Typography>
            )}
            <EmbargoWarning
              embargoInfo={latestEmbargoContent}
              preferredTimezone={publicationSettingsResp?.publicationSetting.preferredTimezone}
              dataTestId="off-platform-embargo-warning"
            />
          </Group>

          <PublishingButtons
            hasCollectionChanged={hasModelChangedOnce}
            isPerformingAction={isMutationInProgress}
            isInEditMode={isInEditMode}
            onPublish={handleClickPublish}
            onSchedule={handleScheduleClick}
            onCancelEdit={hideEditSchedule}
            onDelete={handleDeleteSchedule}
            isScheduling={isScheduling}
            isPublishing={isPublishing}
            isDeleting={isDeleting}
          />
        </Wrapper>
      </WidePageWrapper.Header>
      <WidePageWrapper.Left>{renderEntity(data, { hierarchyId: '', isInEditMode })}</WidePageWrapper.Left>
      <WidePageWrapper.Right>
        <MultiTabSection
          isInEditMode={isInEditMode}
          isRecentlyPublish={isRecentlyPublish}
          isRecentlyUpdated={isRecentlyUpdated}
          onUpdateName={handleUpdateName}
          handleShowHistoryEditMode={showEditSchedule}
          savedForScheduleEdit={savedForScheduleEdit}
        />
      </WidePageWrapper.Right>
    </WidePageWrapper>
  );
};
